// EditTimetable.js - UPDATED to NOT store faculty data

import React, { useState, useMemo, useEffect } from 'react';
import api from '../../../../api';
import { FaPlus, FaCheckCircle, FaCalendarAlt, FaSave, FaTrash, FaEdit, FaClock, FaEye, FaExclamationTriangle } from 'react-icons/fa';
import DayColumn from './DayColumn';
import SearchBar from './SearchBar';
import { Select, Modal, Button, message, Input } from 'antd';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EditTimetable = (props) => {
  const {
    weekTemplate,
    setWeekTemplate,
    subjects,
    labBatches,
    selectedClassId,
    className,
    tenantId,
    organizationId,
    selectedDepartmentId,
    selectedAcademicYearId,
    selectedAcademicYear,
    selectedSemesterId,
    selectedSemesterName,
    selectedTerm,
    isSemesterBased,
    onSave,
    onSubjectChange,
    loadingButton,
    deleteWeekTemplateEntry,
    onOverrideClick,
    electiveGroups = [],
    loadingElectives = false,
    createdBy,
    createdByName,
    isDateWithinAcademicYear = () => true
  } = props;

  const [showWarningBanner, setShowWarningBanner] = useState(false);
  const [warningConflicts, setWarningConflicts] = useState([]);

  const [slotTemplates, setSlotTemplates] = useState([]);
  const [showCreateSlotModal, setShowCreateSlotModal] = useState(false);
  const [showViewTemplatesModal, setShowViewTemplatesModal] = useState(false);
  const [showEditSlotModal, setShowEditSlotModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [slotTimeSlots, setSlotTimeSlots] = useState([]);
  const [editingTimeSlotIndex, setEditingTimeSlotIndex] = useState(null);
  const [timeSlotForm, setTimeSlotForm] = useState({ startTime: '09:00', endTime: '10:00', type: 'Period' });
  const [savingSlotTemplate, setSavingSlotTemplate] = useState(false);
  const [updatingSlotTemplate, setUpdatingSlotTemplate] = useState(false);
  const [deletingSlotTemplate, setDeletingSlotTemplate] = useState(false);
  const [applyingSlotTemplate, setApplyingSlotTemplate] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeDay, setActiveDay] = useState('Monday');
  const [previousWeekTemplate, setPreviousWeekTemplate] = useState(null);

  const fetchSlotTemplates = React.useCallback(async () => {
    try {
      const res = await api.get('/api/timetable-templates', {
        params: { 
          tenantId, 
          departmentId: selectedDepartmentId, 
          academicYearId: selectedAcademicYearId, 
          type: 'slot' 
        }
      });
      setSlotTemplates(res.data?.data || res.data?.templates || []);
    } catch (err) {
      setSlotTemplates([]);
    }
  }, [tenantId, selectedDepartmentId, selectedAcademicYearId]);

  useEffect(() => {
    fetchSlotTemplates();
  }, [tenantId, selectedDepartmentId, selectedAcademicYearId, fetchSlotTemplates]);

  useEffect(() => {
    if (showWarningBanner) {
      const timer = setTimeout(() => {
        setShowWarningBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showWarningBanner]);

  const filteredSubjects = useMemo(() => {
    if (!Array.isArray(subjects) || subjects.length === 0) return [];
    if (!Array.isArray(electiveGroups) || electiveGroups.length === 0) return subjects;

    const groupedSubjectIds = new Set();
    electiveGroups.forEach(group => {
      if (Array.isArray(group.subjects)) {
        group.subjects.forEach(subject => {
          if (subject.subjectId) {
            groupedSubjectIds.add(String(subject.subjectId));
          }
        });
      }
    });

    return subjects.filter(subject => {
      const subjectId = String(subject.id || subject._id);
      return !groupedSubjectIds.has(subjectId);
    });
  }, [subjects, electiveGroups]);

  const addEntry = (day) => {
    const newEntry = {
      type: 'Period',
      dayOfWeek: day,
      timeSlot: { start: '09:00', end: '09:45' },
      subjectId: null,
      subjectCode: '',
      subjectName: '',
      // IMPORTANT: NO facultyUserId, facultyName, facultyEmail - these come from Class
      room: '',
      electiveGroupId: null,
      batchId: null,
      batchName: '',
      allocations: [],
      electiveSelections: [],
      conflictApprovals: [],
      isNew: true
    };
    setWeekTemplate(prev => [...prev, newEntry]);
  };

  const updateEntry = (index, field, value) => {
    const updatedTemplate = [...weekTemplate];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (!updatedTemplate[index][parent]) updatedTemplate[index][parent] = {};
      updatedTemplate[index][parent][child] = value;
    } else {
      updatedTemplate[index][field] = value;
    }
    setWeekTemplate(updatedTemplate);
  };

  const removeEntry = async (index) => {
    const entry = weekTemplate[index];
    if (entry && entry._id) {
      await deleteWeekTemplateEntry(entry._id);
    } else {
      setWeekTemplate(prev => prev.filter((_, i) => i !== index));
    }
  };

  // CRITICAL: Prepare week template WITHOUT any faculty fields
  const prepareWeekTemplateForSave = (weekTemplate) => {
    return weekTemplate.map((e) => {
      const cleaned = { ...e };
      delete cleaned.isNew;
      delete cleaned._id;
      // Remove faculty fields completely
      delete cleaned.facultyUserId;
      delete cleaned.facultyName;
      delete cleaned.facultyEmail;
      
      ['subjectId', 'batchId', 'electiveGroupId'].forEach(field => {
        if (cleaned[field] === '') cleaned[field] = null;
      });
      
      if (Array.isArray(cleaned.allocations)) {
        cleaned.allocations = cleaned.allocations.map(a => {
          const alloc = { ...a };
          delete alloc._id;
          delete alloc.facultyUserId;
          delete alloc.facultyName;
          ['subjectId', 'batchId'].forEach(f => {
            if (alloc[f] === '') alloc[f] = null;
          });
          return alloc;
        });
      }
      
      if (Array.isArray(cleaned.electiveSelections)) {
        cleaned.electiveSelections = cleaned.electiveSelections.map(es => {
          const selection = { ...es };
          delete selection._id;
          if (selection.subjectId === '') selection.subjectId = null;
          if (selection.facultyId === '') selection.facultyId = null;
          if (Array.isArray(selection.subgroupSelections)) {
            selection.subgroupSelections = selection.subgroupSelections.map(sg => {
              const subgroup = { ...sg };
              delete subgroup._id;
              if (subgroup.facultyId === '') subgroup.facultyId = null;
              return subgroup;
            });
          }
          return selection;
        });
      }
      
      return cleaned;
    });
  };

  const handleSaveClick = async () => {
    const cleanedWeekTemplate = prepareWeekTemplateForSave(weekTemplate);
    const payload = {
      tenantId,
      organizationId: organizationId || '',
      classId: selectedClassId,
      className: className || '',
      academicYearId: selectedAcademicYearId,
      academicYearName: selectedAcademicYear?.name || '',
      weekTemplate: cleanedWeekTemplate,
      createdBy: createdBy || null,
      createdByName: createdByName || '',
      updatedBy: createdBy || null,
      updatedByName: createdByName || ''
    };
    
    if (isSemesterBased) {
      payload.semesterType = selectedSemesterId || null;
      payload.semesterName = selectedSemesterName || '';
    } else {
      payload.termId = selectedTerm?._id || null;
      payload.termNumber = selectedTerm?.termNumber || null;
      payload.termName = selectedTerm?.termLabel || selectedTerm?.termName || null;
    }
    
    if (selectedDepartmentId) {
      payload.departmentId = selectedDepartmentId;
    }
    
    try {
      if (typeof onSave === 'function') {
        const response = await onSave(payload);
        
        if (response?.warnings && response.warnings.length > 0) {
          setWarningConflicts(response.warnings);
          setShowWarningBanner(true);
          message.warning({
            content: `Timetable saved with ${response.warnings.length} conflict(s)`,
            duration: 3,
          });
        } else if (response?.hasWarnings === false) {
          message.success('Timetable saved successfully with no conflicts');
        } else {
          message.success('Timetable saved successfully');
        }
      }
    } catch (err) {
      console.error('[EditTimetable] Save error:', err);
      
      if (err.response?.data?.warnings && err.response.data.warnings.length > 0) {
        setWarningConflicts(err.response.data.warnings);
        setShowWarningBanner(true);
        message.warning({
          content: `Timetable saved but ${err.response.data.warnings.length} conflict(s) detected`,
          duration: 3,
        });
      } else if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else {
        message.error('Failed to save timetable');
      }
    }
  };

  const getSubjectName = (subjectId) => {
    const subject = (subjects || []).find(
      s => String(s._id || s.id) === String(subjectId)
    );
    if (!subject) return 'Unknown Subject';
    return subject.name || subject.subjectName;
  };

  const filteredWeekTemplate = weekTemplate.filter(entry => 
    entry.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.subjectId && getSubjectName(entry.subjectId).toLowerCase().includes(searchTerm.toLowerCase())) ||
    (entry.room && entry.room.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const entriesByDay = daysOfWeek.reduce((acc, day) => {
    acc[day] = filteredWeekTemplate.filter(entry => entry.dayOfWeek === day);
    return acc;
  }, {});

  const totalEntries = weekTemplate.length;

  const handleAddTimeSlot = () => {
    if (!timeSlotForm.startTime || !timeSlotForm.endTime) {
      message.error('Please set start and end time');
      return;
    }
    if (timeSlotForm.startTime >= timeSlotForm.endTime) {
      message.error('Start time must be before end time');
      return;
    }
    
    if (editingTimeSlotIndex !== null) {
      const updatedSlots = [...slotTimeSlots];
      updatedSlots[editingTimeSlotIndex] = { ...timeSlotForm };
      setSlotTimeSlots(updatedSlots);
      setEditingTimeSlotIndex(null);
    } else {
      setSlotTimeSlots([...slotTimeSlots, { ...timeSlotForm }]);
    }
    setTimeSlotForm({ startTime: '09:00', endTime: '10:00', type: 'Period' });
  };

  const handleEditTimeSlot = (index) => {
    setEditingTimeSlotIndex(index);
    setTimeSlotForm({ ...slotTimeSlots[index] });
  };

  const handleRemoveTimeSlot = (index) => {
    setSlotTimeSlots(slotTimeSlots.filter((_, i) => i !== index));
    if (editingTimeSlotIndex === index) {
      setEditingTimeSlotIndex(null);
      setTimeSlotForm({ startTime: '09:00', endTime: '10:00', type: 'Period' });
    }
  };

  const handleCreateSlotTemplate = async () => {
    if (!editingTemplate?.name?.trim()) {
      message.error('Please enter a template name');
      return;
    }
    if (slotTimeSlots.length === 0) {
      message.error('Please add at least one time slot');
      return;
    }

    setSavingSlotTemplate(true);
    try {
      let validTenantId = tenantId;
      if (tenantId && typeof tenantId === 'object' && tenantId.$oid) {
        validTenantId = tenantId.$oid;
      }
      
      await api.post('/api/timetable-templates', {
        name: editingTemplate.name,
        slots: slotTimeSlots,
        tenantId: validTenantId,
        departmentId: selectedDepartmentId,
        academicYearId: selectedAcademicYearId,
        type: 'slot',
      });
      
      message.success('Slot template created successfully!');
      setShowCreateSlotModal(false);
      setEditingTemplate(null);
      setSlotTimeSlots([]);
      fetchSlotTemplates();
    } catch (err) {
      message.error('Failed to create slot template');
      console.error(err);
    }
    setSavingSlotTemplate(false);
  };

  const handleUpdateSlotTemplate = async () => {
    if (!editingTemplate?.name?.trim()) {
      message.error('Please enter a template name');
      return;
    }
    if (slotTimeSlots.length === 0) {
      message.error('Please add at least one time slot');
      return;
    }
    
    setUpdatingSlotTemplate(true);
    try {
      await api.put(`/api/timetable-templates/${editingTemplate._id}`, {
        name: editingTemplate.name,
        slots: slotTimeSlots,
        type: 'slot',
      });
      message.success('Slot template updated successfully!');
      setShowEditSlotModal(false);
      setEditingTemplate(null);
      setSlotTimeSlots([]);
      fetchSlotTemplates();
    } catch (err) {
      message.error('Failed to update slot template');
      console.error(err);
    }
    setUpdatingSlotTemplate(false);
  };

  const handleDeleteSlotTemplate = async (templateId) => {
    if (!templateId) return;
    
    setDeletingSlotTemplate(true);
    try {
      await api.delete(`/api/timetable-templates/${templateId}`);
      message.success('Slot template deleted successfully');
      fetchSlotTemplates();
    } catch (err) {
      message.error('Failed to delete slot template');
      console.error(err);
    }
    setDeletingSlotTemplate(false);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setSlotTimeSlots(template.slots || []);
    setShowEditSlotModal(true);
  };

  const handleApplySlotTemplateToDay = async (day, templateId, replaceMode = false) => {
    if (!templateId) {
      message.warning('Please select a slot template');
      return;
    }
    
    setApplyingSlotTemplate(true);
    try {
      const selectedTemplate = slotTemplates.find(t => t._id === templateId);
      if (!selectedTemplate?.slots) {
        message.error('Invalid template');
        return;
      }
      
      let updatedWeek = [...weekTemplate];
      
      if (replaceMode) {
        updatedWeek = weekTemplate.filter(entry => entry.dayOfWeek !== day);
      }
      
      const existingEntries = updatedWeek.filter(entry => entry.dayOfWeek === day);
      const existingTimeKeys = new Set(
        existingEntries.map(entry => `${entry.timeSlot.start}-${entry.timeSlot.end}`)
      );
      
      const newDayEntries = [];
      const skippedSlots = [];
      
      selectedTemplate.slots.forEach(slot => {
        const timeKey = `${slot.startTime}-${slot.endTime}`;
        if (!existingTimeKeys.has(timeKey)) {
          newDayEntries.push({
            type: slot.type || 'Period',
            dayOfWeek: day,
            timeSlot: { start: slot.startTime, end: slot.endTime },
            subjectId: null,
            subjectCode: '',
            subjectName: '',
            // NO faculty fields
            room: '',
            electiveGroupId: null,
            batchId: null,
            batchName: '',
            allocations: [],
            electiveSelections: [],
            conflictApprovals: [],
            isNew: true
          });
        } else {
          skippedSlots.push(`${slot.startTime}-${slot.endTime}`);
        }
      });
      
      updatedWeek = [...updatedWeek, ...newDayEntries];
      
      updatedWeek.sort((a, b) => {
        if (a.dayOfWeek !== b.dayOfWeek) return 0;
        return a.timeSlot.start.localeCompare(b.timeSlot.start);
      });
      
      setWeekTemplate(updatedWeek);
      setPreviousWeekTemplate(weekTemplate);
      
      if (newDayEntries.length > 0) {
        if (skippedSlots.length > 0) {
          message.success(`Added ${newDayEntries.length} new time slots to ${day}. Skipped ${skippedSlots.length} existing slots.`);
        } else {
          message.success(`Added ${newDayEntries.length} time slots to ${day} successfully!`);
        }
      } else {
        message.info(`All time slots from this template already exist in ${day}. No new slots added.`);
      }
      
    } catch (err) {
      message.error('Failed to apply slot template to day');
      console.error(err);
    }
    setApplyingSlotTemplate(false);
  };

  const handleUndo = () => {
    if (previousWeekTemplate) {
      setWeekTemplate(previousWeekTemplate);
      setPreviousWeekTemplate(null);
      message.info('Reverted to previous timetable.');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* Warning Banner */}
      {showWarningBanner && warningConflicts.length > 0 && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow-lg p-4 animate-slide-down">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  ⚠️ Timetable saved with {warningConflicts.length} conflict(s)
                </p>
                <div className="mt-1 text-xs text-yellow-700">
                  {warningConflicts.slice(0, 3).map((conflict, idx) => (
                    <div key={idx} className="mt-1">
                      • {conflict.message || `${conflict.facultyName} has conflict`}
                    </div>
                  ))}
                  {warningConflicts.length > 3 && (
                    <div className="mt-1">• And {warningConflicts.length - 3} more...</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowWarningBanner(false)}
                className="ml-auto flex-shrink-0 text-yellow-500 hover:text-yellow-700"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Slot Template Management Section */}
      <div className="max-w-7xl mx-auto px-4 pt-6 pb-2">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <FaClock className="text-indigo-600" />
              <h4 className="font-semibold text-gray-800">Time Slot Templates</h4>
              <span className="text-xs text-gray-500">({slotTemplates.length} templates)</span>
            </div>
            <div className="flex gap-2">
              {previousWeekTemplate && (
                <Button danger onClick={handleUndo}>
                  Undo Last Change
                </Button>
              )}
              <Button icon={<FaEye />} onClick={() => setShowViewTemplatesModal(true)}>
                View All Templates
              </Button>
              <Button
                type="primary"
                icon={<FaPlus />}
                onClick={() => {
                  setEditingTemplate({ name: '', _id: null });
                  setSlotTimeSlots([]);
                  setEditingTimeSlotIndex(null);
                  setTimeSlotForm({ startTime: '09:00', endTime: '10:00', type: 'Period' });
                  setShowCreateSlotModal(true);
                }}
              >
                Create New Template
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* View All Templates Modal */}
      <Modal
        open={showViewTemplatesModal}
        onCancel={() => setShowViewTemplatesModal(false)}
        title="All Time Slot Templates"
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowViewTemplatesModal(false)}>
            Close
          </Button>
        ]}
      >
        <div className="max-h-96 overflow-y-auto">
          {slotTemplates.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No time slot templates created yet. Click "Create New Template" to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {slotTemplates.map(template => (
                <div key={template._id} className="border rounded-lg p-4 bg-gray-50 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h5 className="font-semibold text-gray-800 text-lg">{template.name}</h5>
                      <div className="text-xs text-gray-500 mt-1">
                        {template.slots?.length || 0} time slots
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="small"
                        type="primary"
                        icon={<FaEdit />}
                        onClick={() => {
                          setShowViewTemplatesModal(false);
                          handleEditTemplate(template);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<FaTrash />}
                        onClick={() => handleDeleteSlotTemplate(template._id)}
                        loading={deletingSlotTemplate}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  {template.slots && template.slots.length > 0 && (
                    <div className="mt-2">
                      <div className="text-sm font-medium text-gray-700 mb-2">Time Slots:</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {template.slots.map((slot, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div>
                              <span className="font-medium">{slot.startTime} - {slot.endTime}</span>
                              <span className="ml-2 text-sm text-gray-600">({slot.type})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create/Edit Time Slot Template Modal */}
      <Modal
        open={showCreateSlotModal || showEditSlotModal}
        onCancel={() => {
          setShowCreateSlotModal(false);
          setShowEditSlotModal(false);
          setEditingTemplate(null);
          setSlotTimeSlots([]);
          setEditingTimeSlotIndex(null);
          setTimeSlotForm({ startTime: '09:00', endTime: '10:00', type: 'Period' });
        }}
        title={showCreateSlotModal ? "Create Time Slot Template" : "Edit Time Slot Template"}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => {
            setShowCreateSlotModal(false);
            setShowEditSlotModal(false);
            setEditingTemplate(null);
            setSlotTimeSlots([]);
          }}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={showCreateSlotModal ? handleCreateSlotTemplate : handleUpdateSlotTemplate}
            loading={savingSlotTemplate || updatingSlotTemplate}
            disabled={!editingTemplate?.name?.trim() || slotTimeSlots.length === 0}
          >
            {showCreateSlotModal ? 'Create Template' : 'Update Template'}
          </Button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
            <Input
              value={editingTemplate?.name || ''}
              onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
              placeholder="Enter template name"
              disabled={savingSlotTemplate || updatingSlotTemplate}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Slots</label>
            <div className="border rounded-lg p-3 bg-gray-50 mb-3">
              <div className="grid grid-cols-3 gap-2 mb-2">
                <Input
                  type="time"
                  value={timeSlotForm.startTime}
                  onChange={e => setTimeSlotForm({ ...timeSlotForm, startTime: e.target.value })}
                  placeholder="Start Time"
                  disabled={savingSlotTemplate || updatingSlotTemplate}
                />
                <Input
                  type="time"
                  value={timeSlotForm.endTime}
                  onChange={e => setTimeSlotForm({ ...timeSlotForm, endTime: e.target.value })}
                  placeholder="End Time"
                  disabled={savingSlotTemplate || updatingSlotTemplate}
                />
                <Select
                  value={timeSlotForm.type}
                  onChange={val => setTimeSlotForm({ ...timeSlotForm, type: val })}
                  disabled={savingSlotTemplate || updatingSlotTemplate}
                >
                  <Select.Option value="Period">Period</Select.Option>
                  <Select.Option value="Lab">Lab</Select.Option>
                  <Select.Option value="Break">Break</Select.Option>
                </Select>
              </div>
              <Button
                type="dashed"
                onClick={handleAddTimeSlot}
                disabled={savingSlotTemplate || updatingSlotTemplate}
                block
              >
                {editingTimeSlotIndex !== null ? 'Update Time Slot' : 'Add Time Slot'}
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {slotTimeSlots.map((slot, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-white border rounded">
                  <div>
                    <span className="font-medium">{slot.startTime} - {slot.endTime}</span>
                    <span className="ml-2 text-sm text-gray-600">({slot.type})</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => handleEditTimeSlot(index)}
                      disabled={savingSlotTemplate || updatingSlotTemplate}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<FaTrash />}
                      onClick={() => handleRemoveTimeSlot(index)}
                      disabled={savingSlotTemplate || updatingSlotTemplate}
                    />
                  </div>
                </div>
              ))}
              {slotTimeSlots.length === 0 && (
                <div className="text-center text-gray-500 py-4">
                  No time slots added. Click "Add Time Slot" to create one.
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
      
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
              <FaCalendarAlt size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 leading-tight">Weekly Timetable</h3>
              <p className="text-xs text-gray-500">
                {totalEntries} sessions scheduled • 
                {filteredSubjects.length} core subjects • 
                {electiveGroups.length} elective groups
                {selectedTerm && !isSemesterBased && <span className="ml-2 text-blue-500">• {selectedTerm.termLabel || selectedTerm.termName}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <button 
              onClick={handleSaveClick}
              className={`hidden md:flex px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all items-center font-medium
                ${loadingButton === 'save' ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={!selectedClassId || loadingButton === 'save'}
            >
              {loadingButton === 'save' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              ) : <FaCheckCircle className="mr-2" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Mobile Tabs */}
        <div className="md:hidden overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <div className="flex gap-2">
            {daysOfWeek.map(day => (
              <button
                key={day}
                onClick={() => setActiveDay(day)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeDay === day 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {daysOfWeek.map(day => (
            <div 
              key={day} 
              className={`transition-all duration-300 ${
                window.innerWidth < 768 && activeDay !== day ? 'hidden' : 'block'
              }`}
            >
              <DayColumn
                day={day}
                entries={entriesByDay[day] || []}
                weekTemplate={weekTemplate}
                subjects={subjects}
                labBatches={labBatches}
                onAddEntry={addEntry}
                onUpdateEntry={updateEntry}
                onRemoveEntry={removeEntry}
                onSubjectChange={onSubjectChange}
                getSubjectName={getSubjectName}
                tenantId={tenantId}
                selectedDepartmentId={selectedDepartmentId}
                selectedAcademicYearId={selectedAcademicYearId}
                selectedAcademicYear={selectedAcademicYear}
                selectedSemesterId={selectedSemesterId}
                selectedClassId={selectedClassId}
                electiveGroups={electiveGroups}
                loadingElectives={loadingElectives}
                slotTemplates={slotTemplates}
                onApplySlotTemplate={handleApplySlotTemplateToDay}
                applyingSlotTemplate={applyingSlotTemplate}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Floating Action Buttons for Mobile */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 md:hidden z-40">
        {onOverrideClick && (
          <button 
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              if (!isDateWithinAcademicYear(today)) {
                console.warn('Selected date is outside academic year');
              }
              onOverrideClick(today);
            }}
            className="w-12 h-12 bg-orange-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-orange-600 transition-all hover:scale-110 active:scale-95"
            title="Create Daily Override"
          >
            <FaPlus />
          </button>
        )}
        <button 
          onClick={handleSaveClick}
          className="w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg shadow-indigo-300 flex items-center justify-center hover:bg-indigo-700 transition-all hover:scale-110 active:scale-95"
          disabled={loadingButton === 'save'}
          title="Save Timetable"
        >
          {loadingButton === 'save' ? (
             <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          ) : <FaSave size={20} />}
        </button>
      </div>
    </div>
  );
};

export default EditTimetable;